import {
  Translated,
  OrderOverview,
  PhoneNumberForm,
  PaymentMethodForm,
  SubscriptionFormWrapper,
} from '@recurrent/shared/build/components';

import React, { useState } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { getErrorMessage } from '@recurrent/shared/build/utils/response';
import { getId as getPlanId } from '@recurrent/shared/build/api/accessors/plan';
import { createSubscription as apiCreateSubscription } from '@recurrent/shared/build/api';
import { usePolicyLinks, useConfig } from '@recurrent/shared/build/hooks';

import { fetchFullPricePlans } from '../../api';

const useStyles = makeStyles((theme) => ({
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    marginLeft: theme.spacing(1),
  },
}));

const SubscribeForm = ({ customer, ...props }) => {
  const { loginPage } = useConfig();

  const classes = useStyles(props);
  const [activeStep, setActiveStep] = useState(0);

  const [plan, setPlan] = useState(null);
  const [phone, setPhone] = useState(null);
  const [countryCode, setCountryCode] = useState(null);

  const [subscription, setSubscription] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);

  const [confirmRequestError, setConfirmRequestError] = useState(null);
  const [confirmRequestInProgress, setConfirmRequestInProgress] =
    useState(false);

  const policyLinks = usePolicyLinks();

  const nextStep = () => setActiveStep((step) => step + 1);
  const previousStep = () => setActiveStep((step) => step - 1);

  const onPhoneChange = ({
    countryCode: passedCountryCode,
    phone: passedPhone,
  }) => {
    setPhone(passedPhone);
    setCountryCode(passedCountryCode);

    nextStep();
  };

  const onPlanChange = (planOption) => {
    setPlan(planOption);
  };

  const onOrderConfirm = async () => {
    setConfirmRequestError(null);
    setConfirmRequestInProgress(true);

    const [error, response] = await apiCreateSubscription(
      {
        internalData: {
          phone: `+${countryCode}${phone}`,
        },
        items: [{ plan: getPlanId(plan) }],
      },
      'reactivation_flow'
    );

    if (!error && response) {
      setSubscription(response);
      nextStep();
    } else {
      setConfirmRequestError(error);
    }

    setConfirmRequestInProgress(false);
  };

  const onPaymentSuccess = (incomingSetupIntent) => {
    setPaymentMethod(incomingSetupIntent);
    onOrderConfirm();
  };

  const onOrderConfirmed = () => {
    window.location.href = loginPage;
  };

  switch (activeStep) {
    case 1:
      return (
        <SubscriptionFormWrapper
          maxWidth="100%"
          onSubmit={nextStep}
          onCancel={previousStep}
          onPlanChange={onPlanChange}
          cancelButtonText={<Translated id="back" />}
          submitButtonText={<Translated id="next" />}
          buttonsContainerProps={{ className: classes.buttons }}
          apiFetchPlans={fetchFullPricePlans}
          noMargins
        >
          <Typography color="textSecondary">
            <Translated
              values={policyLinks}
              id="subscription_plan_choice_description_registration"
            />
          </Typography>
        </SubscriptionFormWrapper>
      );
    case 2:
      return (
        <PaymentMethodForm
          showDescriptionText
          defaultPaymentMethod
          enablePhoneCollection
          onCancel={previousStep}
          onSuccess={onPaymentSuccess}
          submitButtonText={<Translated id="next" />}
          cancelButtonText={<Translated id="back" />}
          requestInProgress={confirmRequestInProgress}
          submitButtonProps={{ className: classes.button }}
          errorMessage={getErrorMessage(confirmRequestError)}
          buttonsContainerProps={{ className: classes.buttons }}
          customer={customer}
        />
      );
    case 3:
      return (
        <OrderOverview
          plan={plan}
          onClose={onOrderConfirmed}
          subscription={subscription}
          paymentMethod={paymentMethod}
          extraData={{ phone, countryCode }}
          requestError={confirmRequestError}
        />
      );
    default:
      return (
        <PhoneNumberForm
          phone={phone}
          onCancel={previousStep}
          onSubmit={onPhoneChange}
          countryCode={countryCode}
          submitButtonProps={{ className: classes.button }}
          buttonsContainerProps={{ className: classes.buttons }}
        />
      );
  }
};

export default SubscribeForm;
